import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { PageHeader, Card, Badge, LoadingState, StatePanel } from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import type { SecurityResponse } from '../../lib/types/subscriptionSecurity.js';

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Pilot P0 §2.4 امنیت (Security Console archetype)
 * canonical pattern: current session highlighted + MFA status, split layout
 * state reference: ready · permission_denied · network_error
 *
 * Read-only in this batch — revoke session / enroll or remove MFA factor
 * are mutations the adapter doesn't expose yet (route inventory §Adapters
 * flags the callable but unwired AuthService method for MFA; session revoke
 * needs the same verification before it's wired).
 */
export function SecurityPage() {
  const { slug } = useParams<{ slug: string }>();
  const result = useApiGet<SecurityResponse>(
    slug ? `${tenantBasePath(slug)}/api/v1/admin/security` : null,
    [slug],
  );

  if (!slug) return null;

  if (result.status === 'loading') {
    return (
      <PageWrap>
        <LoadingState />
      </PageWrap>
    );
  }

  if (result.status === 'error' || result.data.phase !== 'ready') {
    const permissionDenied = result.status === 'ready' && result.data.phase === 'permission_denied';
    return (
      <PageWrap>
        <StatePanel
          variant={permissionDenied ? 'permission_denied' : 'network_error'}
          title={permissionDenied ? 'دسترسی به امنیت ندارید' : 'اطلاعات امنیتی قابل نمایش نیست'}
          description={permissionDenied ? undefined : 'دوباره تلاش کنید.'}
        />
      </PageWrap>
    );
  }

  const { sessions, factors } = result.data;

  return (
    <PageWrap>
      <div
        style={{
          display: 'grid',
          gap: 16,
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        }}
      >
        <Card title="نشست‌ها">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sessions.length === 0 ? <span>نشستی یافت نشد.</span> : null}
            {sessions.map((session) => (
              <div
                key={session.sessionId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  border: session.isCurrent
                    ? '1px solid var(--sak-border-accent, currentColor)'
                    : undefined,
                  borderRadius: 8,
                  padding: 8,
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span>
                    {session.isCurrent ? 'این دستگاه' : session.userAgentHint || 'دستگاه نامشخص'}
                  </span>
                  <span style={{ fontSize: 12, opacity: 0.7 }}>
                    ایجاد {session.createdAtFormatted} · انقضا {session.expiresAtFormatted}
                  </span>
                </div>
                <Badge tone={session.statusBadgeKind}>{session.statusLabel}</Badge>
              </div>
            ))}
          </div>
        </Card>
        <Card title="احراز هویت دومرحله‌ای">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {factors.length === 0 ? <span>هیچ روش احراز فعالی ثبت نشده.</span> : null}
            {factors.map((factor) => (
              <div
                key={factor.factorId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                }}
              >
                <span>{factor.kindLabel}</span>
                <Badge tone={factor.statusBadgeKind}>{factor.statusLabel}</Badge>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div style={{ marginTop: 24 }}>
        <AuditLogSection slug={slug} />
      </div>
    </PageWrap>
  );
}

interface AuditEventRow {
  readonly id: string;
  readonly actorName: string;
  readonly action: string;
  readonly entity: string;
  readonly recordId: string | null;
  readonly occurredAt: string;
}

interface AuditListResponse {
  readonly items: readonly AuditEventRow[];
  readonly total: number;
}

function AuditLogSection({ slug }: { readonly slug: string }) {
  const auditResult = useApiGet<AuditListResponse>(
    `${tenantBasePath(slug)}/api/v1/admin/audit?limit=15`,
    [slug],
  );

  return (
    <Card title="گزارش ممیزی و رویدادهای امنیتی (Audit Log)">
      {auditResult.status === 'loading' && <LoadingState />}
      {auditResult.status === 'error' && (
        <span style={{ color: 'var(--sk-color-muted, #64748b)' }}>
          اطلاعات ممیزی در دسترس نیست یا مجوز مشاهده آن وجود ندارد.
        </span>
      )}
      {auditResult.status === 'ready' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {auditResult.data.items.length === 0 ? (
            <span>هیچ رویدادی ثبت نشده است.</span>
          ) : (
            auditResult.data.items.map((event) => (
              <div
                key={event.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '8px 12px',
                  borderRadius: 6,
                  backgroundColor: 'var(--sk-bg-subtle, #f8fafc)',
                  border: '1px solid var(--sk-border-subtle, #e2e8f0)',
                  fontSize: 13,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 600 }}>{event.actorName}</span>
                  <span style={{ color: 'var(--sk-color-muted, #64748b)' }}>·</span>
                  <span className="ltr font-mono" style={{ direction: 'ltr' }}>
                    {event.action}
                  </span>
                  {event.recordId && (
                    <span
                      style={{
                        fontSize: 12,
                        padding: '1px 6px',
                        borderRadius: 4,
                        backgroundColor: 'var(--sk-bg-canvas, #ffffff)',
                        border: '1px solid var(--sk-border-subtle, #e2e8f0)',
                      }}
                    >
                      {event.recordId}
                    </span>
                  )}
                </div>
                <span
                  style={{
                    fontSize: 12,
                    color: 'var(--sk-color-muted, #64748b)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {new Date(event.occurredAt).toLocaleString('fa-IR')}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </Card>
  );
}

function PageWrap({ children }: { children: ReactNode }) {
  return (
    <div style={{ padding: 24 }}>
      <PageHeader title="امنیت" subtitle="نشست‌های فعال و احراز هویت دومرحله‌ای" />
      {children}
    </div>
  );
}
